import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfigData, IContact, UserData } from 'apps/idl/src/model';
import { ITerminal } from 'apps/idl/src/modules/sce/models/iterminal';
import { SceService } from 'apps/idl/src/modules/sce/services/sce.service';
import { DestroyableComponentBase } from 'apps/idl/src/modules/utils/components/destroyable-component-base';
import { PlatformService } from 'apps/idl/src/services/security';
import { CapacitorPluginScePlugin, StatusSCEPlugin } from 'capacitor-plugin-sce';
import { EMPTY, from, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SceEventService } from '../../../../sce4Mobile/sce-event.service';
import { SceStatus } from '../../../../sce4Mobile/sce-status';
import { SceFactory } from '../../../../sce4Mobile/sceFactory';
import { EDeviceType } from '../../../enums/EDeviceType';
import { EModule } from '../../../enums/EModule';
import { DrawerPopoverService } from '../../../services/drawer-popover.service';
import { LecteurStorageService } from '../../../services/lecteurStorage.service';
import { LoaderService } from '../../../services/loader.service';
import { PanneauService } from '../../../services/panneau.service';
import { SnackbarService } from '../../../services/snackbar.service';
import { ConfirmationSuppressionComponent } from '../../confirmation-suppression/confirmation-suppression.component';

@Component({
	selector: 'di-panneau-ajout-appareil',
	templateUrl: './panneau-ajout-appareil.component.html',
	styleUrls: ['./panneau-ajout-appareil.component.scss'],
})
export class PanneauAjoutAppareilComponent extends DestroyableComponentBase implements OnInit{

	@Input() terminal?: ITerminal;
	@Input() currentUser: IContact;
	public form: FormGroup;
	private sceStatusSubscription?: Subscription;
	public pluginSce : CapacitorPluginScePlugin;
	public deviceTypeUser: EDeviceType;
	
	constructor(
		private fb: FormBuilder,
		private svcPanneau: PanneauService,
		private svcSce: SceService,
		private svcPlatform: PlatformService,
		private svcSnackbar: SnackbarService,
		private svcLoader: LoaderService,
		private svcDrawerPopover: DrawerPopoverService,
		private svcLecteurStorage: LecteurStorageService,
		private svcSceEvent: SceEventService,
		private cdr: ChangeDetectorRef
	) { 
		super();
	}

	ngOnInit(): void {
		this.deviceTypeUser = this.svcPlatform.getDeviceType();
		this.pluginSce = SceFactory.getSceInstance(this.deviceTypeUser);
		this.form = this.fb.group({
			name: [this.terminal?.name ?? '', Validators.required],
		});
	}

	public override ngOnDestroy() {
		if (UserData.modules.includes(EModule.sce) && this.svcPlatform.isAndroid) {
			if (this.sceStatusSubscription) {
				this.sceStatusSubscription.unsubscribe();
			}
			from(this.pluginSce.getStatus()).pipe(
				switchMap((status: StatusSCEPlugin) => {
					if (status.connected) {
						return this.pluginSce.disconnect();
					}
					return EMPTY;
				})
			).subscribe();
		}
		super.ngOnDestroy();
	}

	public handleSubmit(event: Event): void {
		event.preventDefault();
		if(!this.terminal){
			this.createLecteur();
		}else{
			this.modifyLecteur();
		}
	}

	private createLecteur(): void {
		if (!this.currentUser.finess) {
			this.svcSnackbar.showToast(
				"info",
				"bottom center",
				"Numéro de FINESS non défini. Veuillez le renseigner dans 'Mon Compte'",
				null,
				null,
				null,
				"7000"
			);
			this.svcPanneau.close();
		}
		if (this.form.valid) {
			this.svcLoader.showLoader("Ajout de l'appareil...");
			this.svcSce.createLecteur(this.form.value["name"], this.deviceTypeUser).pipe(
				switchMap((terminal: ITerminal) => {
					if (this.deviceTypeUser === EDeviceType.ANDROID || this.deviceTypeUser === EDeviceType.IOS) {
						return from(Promise.all([
							this.svcLecteurStorage.setDeviceId(terminal.terminalId),
							this.pluginSce.setDeviceId({ deviceId: terminal.terminalId })
						])).pipe(
							switchMap(() => this.svcSce.associerLecteurUF(terminal.terminalId, this.currentUser.finess))
						);
					}
					return this.svcSce.associerLecteurUF(terminal.terminalId, this.currentUser.finess);
				}),
				tap(() => {
					this.svcPanneau.close();
					this.svcLoader.hideLoader();
				})
			).subscribe();
		}
	}

	private modifyLecteur(): void {
		if (this.form.valid) {
			this.svcLoader.showLoader("Modification de l'appareil...");
			this.svcSce.modifyLecteur(this.form.value["name"], this.terminal.terminalId).pipe(
				tap(() => {
					this.svcPanneau.close();
					this.svcLoader.hideLoader();
				})
			).subscribe();
		}
	}

	public handleDelete(event: Event): void {
		event.preventDefault();
		if (!this.currentUser.finess) {
			this.svcSnackbar.showToast(
				"info",
				"bottom center",
				"Numéro de FINESS non défini. Veuillez le renseigner dans 'Mon Compte'",
				null,
				null,
				null,
				"7000"
			);
			this.svcPanneau.close();
		}

		this.svcDrawerPopover.open("Confirmer la suppression ?", "auto", event.currentTarget, ConfirmationSuppressionComponent, {
			onConfirm: () => {
				this.svcDrawerPopover.close();
				this.svcLoader.showLoader("Suppression de l'appareil...");
				if(this.terminal.isCurrentDevice && this.terminal.device == EDeviceType.IOS){
					this.listenToSceStatus();
					this.connectToSCE().subscribe();
				}else{
					this.supprimerLecteur().pipe(
						finalize(() => {
							this.close();
						}),
					).subscribe();
				}
			},
			onCancel: () => {
				this.svcDrawerPopover.close();
			}
		})
	}

	public close() {
		this.svcLoader.hideLoader();
		this.svcPanneau.close();
	}

	public connectToSCE(): Observable<void> {
		console.warn('[COMP] Connexion au SCE en cours...');
		return from(this.pluginSce.connect()).pipe(
			takeUntil(this.destroyed$),
			catchError((err) => {
				this.svcLoader.hideLoader();
				console.error('[COMP] Erreur lors du connectToSCE initial', err);
				this.svcSceEvent.emitStatus('error');
				return EMPTY;
			})
		)
	}

	private listenToSceStatus() {
		if (this.sceStatusSubscription) {
			this.sceStatusSubscription.unsubscribe();
			this.sceStatusSubscription = undefined;
		}

		this.sceStatusSubscription = this.svcSceEvent.sceStatus$.pipe(
			filter(Boolean),
			takeUntil(this.destroyed$),
			// timeout(25000),
			switchMap((sceStatus: SceStatus) => this.handleSceStatus(sceStatus)),
			catchError(err => {
				this.svcLoader.hideLoader();
				console.error('[COMP] SCE status handling error:', err.message);
				this.svcSnackbar.showToast("error", "bottom center", "La connexion au lecteur SCE a échoué. Essayez de redémarrer le lecteur ou de relancer l'application.", null, null, null, "6000");
				this.svcPanneau.close();
				this.svcDrawerPopover.close();
				this.cdr.detectChanges();
				return EMPTY;
			})
		).subscribe();
	}

	public handleSceStatus(sceStatus: SceStatus): Observable<SceStatus | void> {
		switch (sceStatus.status) {
			case 'waiting-deviceId':
				this.svcSnackbar.showToast("error", "bottom center", "SCE non utilisable pour le moment. Veuillez enregistrer l'appareil dans Paramétrages > Lecteurs.", null, null, null, "6000");
				return throwError(new Error(`Erreur SCE: ${sceStatus.message ?? "Erreur Inconnue"}`));
			case 'connected':
				console.warn('[COMP] Reset du SCE');
				this.svcSceEvent.removeSceEventListener();
				return this.supprimerLecteur().pipe(
					switchMap(() => from(this.pluginSce.reset())),
					switchMap((result) => {
						if (result) {
							ConfigData.sceInitialize = false;
							return from(this.svcLecteurStorage.removeDeviceId()).pipe(
								switchMap(() => from(this.svcSceEvent.initialize())),
								map(() => sceStatus),
								catchError((err) => {
									console.error("Erreur lors de la suppression de l'appareil");
									return throwError(err)
								}),
								finalize(() => {
									this.close();
								})
							)
						}
						this.svcSnackbar.showToast("error", "bottom center", "L'appareil n'a pas pu être supprimé.", null, null, null, "6000");
						return throwError(new Error(`Erreur SCE: ${sceStatus.message ?? "Erreur Inconnue"}`));
					})
				)
			case 'error':
				console.warn('[COMP] Une erreur a été détectée dans le processus SCE : ' + sceStatus.message);
				this.svcSnackbar.showToast("error", "bottom center", `Erreur critique avec le lecteur : ${sceStatus.message ?? "Erreur Inconnue"}.`, null, null, null, "6000");
				return throwError(new Error(`Erreur SCE: ${sceStatus.message ?? "Erreur Inconnue"}`));
			case 'warning':
				console.warn('[COMP] Une erreur a été détectée dans le processus SCE : ' + sceStatus.message);
				return of(sceStatus);
		}
		this.cdr.detectChanges();
		return of(sceStatus);
	}

	public supprimerLecteur(): Observable<void>{
		return this.svcSce.supprimerAssociationLecteurUF(this.terminal.terminalId, this.currentUser.finess).pipe(
			switchMap(() => this.svcSce.supprimerLecteur(this.terminal.terminalId)),
			catchError((error) => {
				console.error('Erreur lors de la suppression:', error);
				return of(null);
			})
		)
	}
}
